//timesheets.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { useAuth } from "../Context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/Timesheets.css";

/* If your route is different, change this string */
const goBackToTracking = (navigate) => {
  sessionStorage.setItem("admin.returnTab", "attendance");
  localStorage.setItem("admin.activeTab", "attendance");
  navigate("/admin-dashboard", { state: { tab: "attendance" }, replace: true });
};

/* ───────── helpers ───────── */
const startEndFromAnchor = (anchorYYYYMMDD, tz = "UTC") => {
  const anchor = anchorYYYYMMDD
    ? DateTime.fromISO(anchorYYYYMMDD, { zone: tz })
    : DateTime.now().setZone(tz);
  const start = anchor.startOf("day");
  const end = start.plus({ days: 6 }).endOf("day");
  return {
    startISO: start.toISO(),
    endISO: end.toISO(),
    label: `${start.toFormat("dd LLL")} – ${end.toFormat("dd LLL yyyy")}`,
  };
};

const fmt = (iso, tz) =>
  iso
    ? DateTime.fromISO(iso, { zone: tz }).toFormat("ccc, dd LLL • hh:mm a")
    : "—";

/* ───────── component ───────── */
export default function Timesheets() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [companyId, setCompanyId] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [employees, setEmployees] = useState({}); // id → name
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all");
  const [anchorDate, setAnchorDate] = useState(() =>
    DateTime.now().toFormat("yyyy-LL-dd")
  );
  const [approvedFilter, setApprovedFilter] = useState("all"); // all|approved|unapproved
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  /* bootstrap company + tz */
  useEffect(() => {
    if (!user) return;
    (async () => {
      const uref = doc(db, "users", user.uid);
      const usnap = await getDoc(uref);
      const cid = usnap.data()?.companyId || null;
      setCompanyId(cid);
      if (cid) {
        const csnap = await getDoc(doc(db, "companies", cid));
        setCompanyTimeZone(csnap.data()?.timeZone || "UTC");
      }
    })();
  }, [user]);

  /* live employees */
  useEffect(() => {
    if (!companyId) return;
    const empsRef = collection(db, `companies/${companyId}/employees`);
    const unsub = onSnapshot(empsRef, (snap) => {
      const map = {};
      snap.forEach((d) => (map[d.id] = d.data()?.name || "Unknown"));
      setEmployees(map);
      setSelectedEmployeeId((prev) =>
        prev === "all" && snap.docs[0] ? snap.docs[0].id : prev
      );
    });
    return () => unsub();
  }, [companyId]);

  /* load entries for chosen employee + 7-day range */
  useEffect(() => {
    if (!companyId || selectedEmployeeId === "all" || !anchorDate) return;

    const { startISO, endISO } = startEndFromAnchor(
      anchorDate,
      companyTimeZone
    );

    setLoading(true);
    const baseRef = collection(db, `companies/${companyId}/time_tracking`);
    const qRef = query(
      baseRef,
      where("userId", "==", selectedEmployeeId),
      where("date", ">=", startISO.slice(0, 10)),
      where("date", "<=", endISO.slice(0, 10)),
      orderBy("date", "desc"),
      limit(1000)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const rows = snap.docs.map((ds) => ({ id: ds.id, ...ds.data() }));
        setTimeEntries(rows);
        setSelectedIds(new Set()); // reset selection when range/employee changes
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error("Failed to load time entries.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [companyId, selectedEmployeeId, anchorDate, companyTimeZone]);

  /* derived views */
  const filteredEntries = useMemo(() => {
    if (approvedFilter === "approved")
      return timeEntries.filter((e) => !!e.approved);
    if (approvedFilter === "unapproved")
      return timeEntries.filter((e) => !e.approved);
    return timeEntries;
  }, [timeEntries, approvedFilter]);

  const { label } = startEndFromAnchor(anchorDate, companyTimeZone);

  const totals = useMemo(() => {
    let hours = 0,
      edited = 0,
      missing = 0,
      approvedCount = 0;
    for (const e of filteredEntries) {
      if (e.wasEdited) edited++;
      if (!e.clockOut) missing++;
      if (e.approved) approvedCount++;
      hours += Number(e.hoursWorked || 0);
    }
    return {
      hours: Number(hours.toFixed(2)),
      edited,
      missing,
      approvedCount,
      count: filteredEntries.length,
    };
  }, [filteredEntries]);

  /* selection helpers */
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () =>
    setSelectedIds(new Set(filteredEntries.map((e) => e.id)));
  const clearAll = () => setSelectedIds(new Set());

  const missingClockOutSelected = useMemo(() => {
    const map = new Map(filteredEntries.map((e) => [e.id, e]));
    const bad = [];
    selectedIds.forEach((id) => {
      const e = map.get(id);
      if (e && !e.clockOut) bad.push(e);
    });
    return bad;
  }, [filteredEntries, selectedIds]);
  const approveDisabled =
    selectedIds.size === 0 || missingClockOutSelected.length > 0;

  /* approve selected */
  const approveSelected = async () => {
    if (!companyId) return;
    if (approveDisabled) {
      if (selectedIds.size === 0) toast.info("Select at least one row.");
      else
        toast.error("Resolve selections without clock-out before approving.");
      return;
    }
    const approver = {
      uid: user?.uid || null,
      name: user?.email || "manager",
      role: "admin",
    };
    const entriesById = new Map(filteredEntries.map((e) => [e.id, e]));
    const toApprove = [];
    selectedIds.forEach((id) => {
      const e = entriesById.get(id);
      if (e && !e.approved) toApprove.push(id);
    });
    if (toApprove.length === 0) {
      toast.info("Selected rows are already approved.");
      return;
    }
    try {
      const batch = writeBatch(db);
      toApprove.forEach((id) => {
        const ref = doc(db, `companies/${companyId}/time_tracking`, id);
        batch.update(ref, {
          approved: true,
          approvedAt: serverTimestamp(),
          approvedBy: approver,
        });
      });
      await batch.commit();
      toast.success(
        toApprove.length === 1
          ? "1 entry approved."
          : `${toApprove.length} entries approved.`
      );
      setSelectedIds(new Set());
    } catch (e) {
      console.error(e);
      toast.error("Failed to approve selected entries.");
    }
  };

  /* unapprove selected */
  const unapproveSelected = async () => {
    if (!companyId) return;
    if (selectedIds.size === 0) {
      toast.info("Select at least one row.");
      return;
    }
    try {
      const batch = writeBatch(db);
      selectedIds.forEach((id) => {
        const ref = doc(db, `companies/${companyId}/time_tracking`, id);
        batch.update(ref, {
          approved: false,
          approvedAt: null,
          approvedBy: null,
        });
      });
      await batch.commit();
      toast.success(
        selectedIds.size === 1
          ? "1 entry unapproved."
          : `${selectedIds.size} entries unapproved.`
      );
      setSelectedIds(new Set());
    } catch (e) {
      console.error(e);
      toast.error("Failed to unapprove selected entries.");
    }
  };

  /* Export CSV of current filtered entries */
  const exportCsv = () => {
    const headers = [
      "Date",
      "Clock-In",
      "Clock-Out",
      "Hours",
      "Status",
      "Edited",
      "Forced",
      "Approved",
    ];
    const rows = filteredEntries.map((e) => [
      e.date,
      fmt(e.clockIn, companyTimeZone),
      fmt(e.clockOut, companyTimeZone),
      e.hoursWorked ? Number(e.hoursWorked).toFixed(2) : "",
      e.status || "",
      e.wasEdited ? "Yes" : "",
      e.forcedClockOut ? "Yes" : "",
      e.approved ? "Yes" : "",
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `timesheets_${selectedEmployeeId}_${anchorDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ts-page">
      <div className="ts-card">
        {/* TOP BAR — only Back button */}
        <div className="ts-topbar">
          <button
            className="btn back"
            onClick={() => goBackToTracking(navigate)}
            type="button"
            aria-label="Back to Employer Time Tracking"
            title="Back to Employer Time Tracking"
          >
            ← Back to Time Tracking
          </button>
        </div>

        {/* TITLE BAR — title kept separate for breathing room */}
        <div className="ts-titlebar">
          <h2 className="ts-title">Timesheets</h2>
          <div className="ts-titlebar-spacer" />
        </div>
        <div className="ts-body">
          {/* FILTERS GRID */}
          <div className="ts-controls">
            <label className="ts-field">
              <span>Employee</span>
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
              >
                {Object.entries(employees).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <label className="ts-field">
              <span>Anchor date (7-day window)</span>
              <input
                type="date"
                value={anchorDate}
                onChange={(e) => setAnchorDate(e.target.value)}
              />
            </label>

            <div className="ts-field ts-seg">
              <span>Show</span>
              <div className="seg">
                <button
                  type="button"
                  className="seg-btn"
                  data-active={approvedFilter === "all"}
                  onClick={() => setApprovedFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className="seg-btn"
                  data-active={approvedFilter === "approved"}
                  onClick={() => setApprovedFilter("approved")}
                >
                  Approved
                </button>
                <button
                  type="button"
                  className="seg-btn"
                  data-active={approvedFilter === "unapproved"}
                  onClick={() => setApprovedFilter("unapproved")}
                >
                  Unapproved
                </button>
              </div>
            </div>

            {/* Light period card */}
            <div className="ts-period" aria-live="polite">
              <div className="ts-period-badge">Period</div>
              <div className="ts-period-text">{label}</div>
              <div className="ts-period-sub">{companyTimeZone}</div>
            </div>
          </div>

          {/* ACTIONS BAR — sits directly under filters, aligned height */}
          <div
            className="ts-actionsbar"
            role="toolbar"
            aria-label="Bulk actions"
          >
            <div className="btn-row">
              <button
                className="btn ghost"
                type="button"
                onClick={selectAllVisible}
              >
                Select all
              </button>
              <button className="btn ghost" type="button" onClick={clearAll}>
                Clear
              </button>
            </div>
            <div className="btn-row">
              <button
                className={`btn primary ${approveDisabled ? "disabled" : ""}`}
                onClick={approveSelected}
                disabled={approveDisabled}
                title={
                  missingClockOutSelected.length > 0
                    ? "Resolve selections without clock-out first"
                    : undefined
                }
                type="button"
              >
                Approve Selected
              </button>
              <button
                className="btn ghost"
                type="button"
                onClick={unapproveSelected}
              >
                Unapprove Selected
              </button>
              <button className="btn ghost" type="button" onClick={exportCsv}>
                Export CSV
              </button>
            </div>
          </div>

          {/* WARNING BANNER */}
          {missingClockOutSelected.length > 0 && (
            <div className="ts-alert" role="alert">
              <div className="ts-alert-icon">⚠️</div>
              <div className="ts-alert-body">
                <div className="ts-alert-title">
                  {missingClockOutSelected.length} selected entr
                  {missingClockOutSelected.length === 1 ? "y" : "ies"}{" "}
                  {missingClockOutSelected.length === 1 ? "has" : "have"} no
                  clock-out.
                </div>
                <div className="ts-alert-text">
                  Approvals are blocked until every selected row has a
                  clock-out. Deselect these rows or fix the clock-out time.
                </div>
                <div className="ts-alert-list">
                  {missingClockOutSelected.slice(0, 4).map((e) => (
                    <span key={e.id} className="chip warn">
                      {e.date}
                    </span>
                  ))}
                  {missingClockOutSelected.length > 4 && (
                    <span className="chip warn">+ more…</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STATS */}
          <div className="ts-stats">
            <div className="stat">
              <div className="stat-k">Entries</div>
              <div className="stat-v">{totals.count}</div>
            </div>
            <div className="stat">
              <div className="stat-k">Selected</div>
              <div className="stat-v">{selectedIds.size}</div>
            </div>
            <div className="stat">
              <div className="stat-k">Approved</div>
              <div className="stat-v">{totals.approvedCount}</div>
            </div>
            <div className="stat accent">
              <div className="stat-k">Total Hours</div>
              <div className="stat-v">{totals.hours}</div>
            </div>
            <div className="stat warn">
              <div className="stat-k">Edited</div>
              <div className="stat-v">{totals.edited}</div>
            </div>
            <div className="stat warn">
              <div className="stat-k">Missing Clock-Out</div>
              <div className="stat-v">{totals.missing}</div>
            </div>
          </div>

          {/* TABLE */}
          <div className="ts-table-wrap">
            {loading ? (
              <p className="muted">Loading…</p>
            ) : filteredEntries.length === 0 ? (
              <div className="ts-empty">
                <div className="ts-empty-emoji">📄</div>
                <div className="ts-empty-title">
                  No entries for this employee / range
                </div>
                <div className="ts-empty-sub">
                  Try a different week or employee.
                </div>
              </div>
            ) : (
              <div className="ts-scroll">
                <table className="ts-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Date</th>
                      <th>Clock-In</th>
                      <th>Clock-Out</th>
                      <th>Hours</th>
                      <th>Status</th>
                      <th>Edited?</th>
                      <th>Forced?</th>
                      <th>Approved?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map((e) => (
                      <tr
                        key={e.id}
                        className={selectedIds.has(e.id) ? "is-selected" : ""}
                      >
                        <td className="sel-cell">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(e.id)}
                            onChange={() => toggleOne(e.id)}
                            aria-label={`Select row for ${e.date}`}
                          />
                        </td>
                        <td>{e.date}</td>
                        <td>{fmt(e.clockIn, companyTimeZone)}</td>
                        <td>{fmt(e.clockOut, companyTimeZone)}</td>
                        <td>
                          {e.hoursWorked
                            ? Number(e.hoursWorked).toFixed(2)
                            : "-"}
                        </td>
                        <td>
                          <span
                            className={`pill status ${e.status
                              ?.toLowerCase()
                              .replace(/\s/g, "-")}`}
                          >
                            {e.status}
                          </span>
                        </td>
                        <td>
                          {e.wasEdited ? <b className="edited">Yes</b> : "—"}
                        </td>
                        <td>
                          {e.forcedClockOut ? (
                            <span className="pill danger">Forced</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {e.approved ? (
                            <span className="pill success">Approved</span>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* STICKY BULK BAR */}
          {selectedIds.size > 0 && (
            <div className="ts-bulkbar" role="region" aria-live="polite">
              <div className="bulk-left">
                <span className="bulk-count">{selectedIds.size}</span> selected
                {missingClockOutSelected.length > 0 && (
                  <span className="bulk-warn">
                    {" "}
                    • {missingClockOutSelected.length} need clock-out
                  </span>
                )}
              </div>
              <div className="bulk-actions">
                <button className="btn ghost" type="button" onClick={clearAll}>
                  Clear
                </button>
                <button
                  className={`btn primary ${approveDisabled ? "disabled" : ""}`}
                  onClick={approveSelected}
                  disabled={approveDisabled}
                  title={
                    approveDisabled
                      ? "Resolve selections without clock-out"
                      : undefined
                  }
                  type="button"
                >
                  Approve Selected
                </button>
                <button
                  className="btn ghost"
                  type="button"
                  onClick={unapproveSelected}
                >
                  Unapprove
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2200} />
    </div>
  );
}
